import { motion } from "framer-motion";

const PORTRAIT_1 = "/images/bejnni-live.jpg";
const PORTRAIT_2 =
    "https://images.unsplash.com/photo-1498798821241-1f327af804fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxtb29keSUyMG11c2ljaWFuJTIwcG9ydHJhaXQlMjBzcG90bGlnaHR8ZW58MHx8fHwxNzg2ODYzODYwfDA&ixlib=rb-4.1.0&q=85";
const CROWD =
    "https://images.unsplash.com/photo-1478147427282-58a87a120781?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwxfHxjb25jZXJ0JTIwY3Jvd2QlMjBkYXJrJTIwbW9vZHl8ZW58MHx8fHwxNzg2ODYzODYwfDA&ixlib=rb-4.1.0&q=85";

const reveal = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1] },
};

const clipReveal = {
    initial: { clipPath: "inset(100% 0 0 0)" },
    whileInView: { clipPath: "inset(0% 0 0 0)" },
    viewport: { once: true, margin: "-100px" },
    transition: { duration: 1.2, ease: [0.16, 1, 0.3, 1] },
};

const Chapter = ({ number, title, children, testId, className = "" }) => (
    <motion.div {...reveal} className={className} data-testid={testId}>
        <p className="mb-6 font-mono text-xs tracking-[0.3em] text-blood">
            {number}.
        </p>
        <h3 className="mb-8 font-serif text-2xl leading-tight tracking-tight sm:text-3xl lg:text-4xl">
            {title}
        </h3>
        <p className="max-w-md text-base leading-relaxed text-zinc-400">
            {children}
        </p>
    </motion.div>
);

const ClippedImage = ({ src, alt, className = "", testId }) => (
    <motion.div
        {...clipReveal}
        className={`group overflow-hidden ${className}`}
        data-testid={testId}
    >
        <img
            src={src}
            alt={alt}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
    </motion.div>
);

export default function ManifestoSection() {
    return (
        <section
            id="world"
            data-testid="manifesto-section"
            className="relative px-6 py-24 md:px-16 md:py-40"
        >
            <div className="absolute left-1/3 top-1/4 h-[50vh] w-[40vw] rounded-full bg-blood/10 blur-[140px]" />

            <motion.p
                {...reveal}
                className="mb-16 font-mono text-xs uppercase tracking-[0.3em] text-zinc-500 md:mb-24"
            >
                02 / The world of bejnni
            </motion.p>

            <div className="grid grid-cols-1 gap-16 md:grid-cols-12 md:gap-8">
                <Chapter
                    number="01"
                    title="The sound"
                    testId="chapter-sound"
                    className="md:col-span-5 md:pt-24"
                >
                    Slow-burning synths, low-end that sits in your chest, and
                    vocals half-whispered like a secret you were never meant to
                    hear. Every track is recorded after midnight and mixed until
                    it feels like one.
                </Chapter>
                <ClippedImage
                    src={PORTRAIT_1}
                    alt="bejnni under a single spotlight"
                    testId="chapter-image-1"
                    className="aspect-[3/4] md:col-span-4 md:col-start-8"
                />
            </div>

            <div className="mt-24 grid grid-cols-1 gap-16 md:mt-40 md:grid-cols-12 md:gap-8">
                <ClippedImage
                    src={PORTRAIT_2}
                    alt="bejnni portrait in the dark"
                    testId="chapter-image-2"
                    className="aspect-[3/4] md:col-span-4 md:col-start-2 md:-mt-16"
                />
                <Chapter
                    number="02"
                    title="The dark"
                    testId="chapter-dark"
                    className="md:col-span-5 md:col-start-7 md:pt-32"
                >
                    Bejnni makes music for the drive home with no destination —
                    streetlights, static, and the red glow of a dashboard. It is
                    not sad music. It is honest music, and honesty keeps late
                    hours.
                </Chapter>
            </div>

            <div className="mt-24 md:mt-40">
                <Chapter
                    number="03"
                    title="The stage"
                    testId="chapter-stage"
                    className="mb-16 md:ml-auto md:mr-0 md:text-left"
                >
                    Live, the songs get louder and the room gets darker. One
                    spotlight, no phones in the air for the first three songs —
                    just a crowd breathing in the same tempo.
                </Chapter>
                <ClippedImage
                    src={CROWD}
                    alt="Crowd at a bejnni show in the dark"
                    testId="chapter-image-3"
                    className="aspect-[16/9] w-full"
                />
            </div>
        </section>
    );
}
